import axios from 'axios';

export type OrganisationBlockedStatus = 'inactive' | 'suspended' | 'blocked';

export class OrganisationBlockedError extends Error {
  readonly status: OrganisationBlockedStatus;

  constructor(status: OrganisationBlockedStatus, message?: string) {
    super(
      message ??
        (status === 'inactive'
          ? 'Your organisation is inactive. Contact your platform administrator.'
          : status === 'suspended'
            ? 'Your organisation has been suspended. Contact your platform administrator.'
            : 'Your organisation is not available. Contact your platform administrator.'),
    );
    this.name = 'OrganisationBlockedError';
    this.status = status;
  }
}

function readErrorPayload(error: unknown): { code?: string; error?: string; message?: string | string[]; organisationStatus?: string } | null {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return null;
  }
  const data = error.response.data;
  if (typeof data === 'string') {
    return { error: data };
  }
  if (data && typeof data === 'object') {
    return data as { code?: string; error?: string; message?: string | string[]; organisationStatus?: string };
  }
  return null;
}

function messageParts(payload: { error?: string; message?: string | string[] } | null): string[] {
  if (!payload) return [];
  return [payload.error, payload.message].flat().filter(Boolean).map(String);
}

function parseStatusFromText(text: string): OrganisationBlockedStatus | null {
  if (/organisation is inactive|organisation is not active/i.test(text)) {
    return 'inactive';
  }
  if (/organisation is suspended|organisation has been suspended/i.test(text)) {
    return 'suspended';
  }
  if (/organisation is (inactive|suspended)/i.test(text)) {
    const match = text.match(/organisation is (inactive|suspended)/i);
    if (match?.[1]?.toLowerCase() === 'inactive') return 'inactive';
    if (match?.[1]?.toLowerCase() === 'suspended') return 'suspended';
  }
  return null;
}

export function getOrganisationBlockedStatus(error: unknown): OrganisationBlockedStatus | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) {
    return null;
  }

  const payload = readErrorPayload(error);
  if (payload?.code === 'ORGANISATION_NOT_ACTIVE') {
    const raw = String(payload.organisationStatus ?? '').trim().toLowerCase();
    if (raw === 'inactive' || raw === 'suspended') {
      return raw;
    }
    return 'blocked';
  }

  const fromText = messageParts(payload)
    .map(parseStatusFromText)
    .find((value): value is OrganisationBlockedStatus => value !== null);
  return fromText ?? null;
}

export function isOrganisationBlockedError(error: unknown): boolean {
  return getOrganisationBlockedStatus(error) !== null;
}

export function getOrganisationBlockedMessage(status: OrganisationBlockedStatus): string {
  return new OrganisationBlockedError(status).message;
}

export function organisationBlockedSignInPath(status: OrganisationBlockedStatus): string {
  const queryStatus = status === 'blocked' ? '' : `&status=${encodeURIComponent(status)}`;
  return `/signin?reason=org_blocked${queryStatus}`;
}
